import { IRequestContext } from '@msdyn365-commerce/core-internal';

import { UCConsentServiceNames } from '../../../integrations/userCentrics/const';
import { UserCentricsService } from '../../../integrations/userCentrics/UserCentricsService';
import { CONSENT_GATED_SERVICES, ConsentGatedCookieNames, CookieNames, DEFAULT_COOKIE_EXPIRATION_DAYS } from './cookie-service.const';

export type CookieName = CookieNames | ConsentGatedCookieNames;

export type SetCookieOptions = {
    expirationDays?: number;
};

export class CookieService {
    private request: IRequestContext;

    constructor(request: IRequestContext) {
        this.request = request;
    }

    public getCookieExpiration(overrideExpirationDays?: number): Date {
        const cookieDurationFromConfig = Number(this.request.app.config.cookieExpiration);
        const expirationDays =
            overrideExpirationDays ?? (isNaN(cookieDurationFromConfig) ? DEFAULT_COOKIE_EXPIRATION_DAYS : cookieDurationFromConfig);

        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + expirationDays);
        return expiryDate;
    }

    public getRequestCookie<T = string>(cookieName: CookieName): T | undefined {
        return this.request.cookies.get<T>(cookieName).value;
    }

    public getCartCookie(): string {
        return this.request.cookies.getCartCookie();
    }

    public async setRequestCookie<T>(cookieName: CookieName, cookieValue: T, options?: SetCookieOptions): Promise<void> {
        await this.syncMsdynUserConsentCookie();
        this.request.cookies.set(cookieName, cookieValue, {
            path: '/',
            expires: this.getCookieExpiration(options?.expirationDays)
        });
    }

    public removeRequestCookie(cookieName: CookieName): void {
        this.request.cookies.remove(cookieName);
    }

    public async syncMsdynUserConsentCookie(): Promise<void> {
        if (!UserCentricsService.hasLoaded()) {
            return;
        }

        const hasConsent = await UserCentricsService.hasAnyNonEssentialConsent();
        if (hasConsent) {
            this.request.cookies.setConsentCookie();
        } else {
            // cookies.deleteConsentCookie() does not work on all pages, so we must delete the cookie by name
            this.removeRequestCookie(CookieNames.UserConsent);
        }
    }

    public async syncConsentGatedCookies(): Promise<void> {
        if (!UserCentricsService.hasLoaded()) {
            return;
        }

        const controllerId = await UserCentricsService.getControllerId();
        if (!controllerId) {
            return;
        }

        CONSENT_GATED_SERVICES.forEach(service => {
            if (service.storeControllerId) {
                this.setConsentGatedCookie(service.serviceName, service.cookieName, controllerId);
            } else {
                this.removeConsentGatedCookieIfNotConsented(service.cookieName, service.serviceName);
            }
        });
    }

    public async getConsentedControllerId(serviceCookie: ConsentGatedCookieNames): Promise<string | undefined> {
        if (typeof window === 'undefined') {
            return this.getRequestCookie(serviceCookie);
        }

        const service = CONSENT_GATED_SERVICES.find(gatedService => gatedService.cookieName === serviceCookie);
        const hasConsent = service && (await UserCentricsService.hasServiceConsent(service.serviceName));
        if (!hasConsent) {
            return undefined;
        }

        return UserCentricsService.getControllerId();
    }

    public async setConsentGatedCookie<T>(
        serviceName: UCConsentServiceNames,
        cookieName: ConsentGatedCookieNames,
        value: T,
        options?: SetCookieOptions
    ): Promise<void> {
        const hasConsent = await UserCentricsService.hasServiceConsent(serviceName);
        if (!hasConsent) {
            this.removeConsentGatedCookie(cookieName);
            return;
        }

        await this.setRequestCookie(cookieName, value, options);
    }

    public removeConsentGatedCookie(cookieName: ConsentGatedCookieNames): void {
        this.removeRequestCookie(cookieName);
    }

    private removeConsentGatedCookieIfNotConsented = async (
        cookieName: ConsentGatedCookieNames,
        serviceName: UCConsentServiceNames
    ): Promise<void> => {
        const hasConsent = await UserCentricsService.hasServiceConsent(serviceName);
        if (!hasConsent) {
            this.removeConsentGatedCookie(cookieName);
        }
    };
}
